import React, {useCallback} from "react";
import {toast} from "react-toastify";
import useTranslation from "../../i18n/useTranslation";
import {getLocalizedGraphQLError} from "../../util/graphqlError";
import {useOrganizationBlankImportExcelTemplate} from "./queries/GetOrganizationImportTemplate";

function base64ToUint8Array(base64) {
    const binary = window.atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }

    return bytes;
}

export default function OrganizationImportTemplateButton({
    organizationId,
    style,
    className = "btn",
}) {
    const {t} = useTranslation();
    const [loadTemplate, {loading}] = useOrganizationBlankImportExcelTemplate();

    const handleDownload = useCallback(async () => {
        if (!organizationId || loading) return;

        try {
            const {data} = await loadTemplate({
                variables: {
                    organizationId: Number(organizationId),
                    maxRows: 100,
                },
            });

            const filePayload = data?.organizationBlankImportExcelTemplate;

            if (!filePayload?.contentBase64) {
                toast.error(t("organizations.importTemplateDownloadError"), {
                    autoClose: 3000,
                    toastId: "organization_import_template_missing_payload",
                    position: "top-center",
                });
                return;
            }

            const bytes = base64ToUint8Array(filePayload.contentBase64);
            const blob = new Blob([bytes], {
                type: filePayload.mimeType ||
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");

            anchor.href = url;
            anchor.download = filePayload.filename || "organization-import-template.xlsx";
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            URL.revokeObjectURL(url);

            toast.success(t("organizations.importTemplateDownloaded"), {
                autoClose: 2500,
                toastId: "organization_import_template_downloaded",
                position: "top-center",
            });
        } catch (error) {
            toast.error(
                getLocalizedGraphQLError(
                    error,
                    t,
                    "organizations.importTemplateDownloadError"
                ),
                {
                    autoClose: 3500,
                    toastId: "organization_import_template_download_error",
                    position: "top-center",
                }
            );
        }
    }, [loadTemplate, loading, organizationId, t]);

    return (
        <button
            type="button"
            className={className}
            style={style}
            onClick={handleDownload}
            disabled={loading}
            title={t("organizations.downloadImportTemplate")}
        >
            <i className="fas fa-file-download" />
            {loading
                ? t("organizations.downloadingImportTemplate")
                : t("organizations.downloadImportTemplate")}
        </button>
    );
}
